import { Box, Button, Grid, Typography, useTheme, useMediaQuery } from '@mui/material';
import { DirectionsBike, TrendingUp, CalendarMonth, Groups, Speed, Analytics, FlashOn, LocalFireDepartment } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

/**
 * LandingPage Component
 * 
 * Public-facing landing page for the Surge PWA.
 * Features hero section, feature highlights, benefits, and call-to-action buttons.
 * Responsive design optimized for mobile, tablet, and desktop viewports.
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const featuresRef = useRef<HTMLDivElement>(null);

  // Ref for the hero visual container to calculate mouse position
  const heroVisualRef = useRef<HTMLDivElement>(null);
  // State for 3D tilt effect
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleGetStarted = () => {
    navigate('/login');
  };

  const handleLearnMore = () => {
    // Scroll to features section
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Generate particles effect
  useEffect(() => {
    const particlesContainer = document.getElementById('particles');
    if (particlesContainer) {
      particlesContainer.innerHTML = '';
      for (let i = 0; i < 50; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.left = `${Math.random() * 100}%`;
        particle.style.animationDelay = `${Math.random() * 3}s`;
        particle.style.animationDuration = `${3 + Math.random() * 2}s`;
        particlesContainer.appendChild(particle);
      }
    }

    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      observer.observe(el);
    });

    return () => {
      document.querySelectorAll('.animate-on-scroll').forEach(el => {
        observer.unobserve(el);
      });
    };
  }, []);

  // Handle 3D Tilt Effect for Hero Visual
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!heroVisualRef.current) return;

    const rect = heroVisualRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;

    // Reduce the intensity by dividing by a larger number (e.g., 25)
    setTilt({ x: x / -25, y: y / 25 });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
  };

  const features = [
    {
      icon: <TrendingUp sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Advanced Analytics',
      description: 'Track your training load (CTL/ATL/TSB), analyze power curves, and monitor performance metrics with comprehensive visualizations.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80'
    },
    {
      icon: <CalendarMonth sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Smart Planning',
      description: 'AI-powered workout planning with structured intervals and adaptive training zones.',
      image: 'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=800&q=80'
    },
    {
      icon: <Groups sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Coach Connect',
      description: 'Collaborate with trainers for personalized guidance and real-time feedback.',
      image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&q=80'
    },
    {
      icon: <Speed sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Zone Mastery',
      description: 'Custom heart rate and pace zones calibrated to your unique physiology.',
      image: 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=800&q=80'
    },
    {
      icon: <Analytics sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Performance AI',
      description: 'Machine learning insights for VO2 max, FTP, and predictive performance modeling.',
      image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80'
    },
    {
      icon: <DirectionsBike sx={{ fontSize: 48, color: theme.palette.primary.main }} />,
      title: 'Offline First',
      description: 'Full functionality anywhere, anytime. Seamless sync when you reconnect.',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&q=80'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      role: 'Marathon Runner',
      text: 'This platform completely transformed how I approach training. The insights are incredible!',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80'
    },
    {
      name: 'Mike T.',
      role: 'Cycling Coach',
      text: 'The coach collaboration features are game-changing. My athletes love it.',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&q=80'
    },
    {
      name: 'Emma R.',
      role: 'Triathlete',
      text: 'Finally, a platform that understands serious athletes. The analytics are next-level.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>
      {/* CSS for animations and styling */}
      <style>{`
        :root {
          --primary: #fc5200;
          --primary-dark: #d44500;
          --primary-light: #ff6b1a;
          --secondary: #00d4ff;
          --dark: #0a0e27;
          --dark-secondary: #151a3a;
          --light: #ffffff;
          --gray: #94a3b8;
        }

        .gradient-bg {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          overflow: hidden;
          background: linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%);
        }

        .gradient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.4;
          animation: float 20s ease-in-out infinite;
        }

        .orb-1 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, var(--primary), transparent);
          top: -200px;
          left: -100px;
          animation-delay: 0s;
        }

        .orb-2 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, var(--secondary), transparent);
          bottom: -150px;
          right: -100px;
          animation-delay: 7s;
        }

        .orb-3 {
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, #8b5cf6, transparent);
          top: 50%;
          left: 50%;
          animation-delay: 14s;
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(100px, -50px) scale(1.1); }
          66% { transform: translate(-50px, 100px) scale(0.9); }
        }

        .particles {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 1;
          pointer-events: none;
        }

        .particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: var(--secondary);
          border-radius: 50%;
          opacity: 0;
          animation: particleFloat 5s ease-in-out infinite;
        }

        @keyframes particleFloat {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% { transform: translateY(-100vh) translateX(50px); opacity: 0; }
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 50px;
          font-size: 14px;
          margin-bottom: 24px;
          backdrop-filter: blur(10px);
        }

        .badge-pulse {
          width: 8px;
          height: 8px;
          background: var(--primary);
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }

        .gradient-text {
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          display: inline-block;
        }

        .hero-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
          gap: 24px;
          padding-top: 24px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .stat-item {
          text-align: center;
        }

        .stat-number {
          font-size: 32px;
          font-weight: 800;
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 12px;
          color: var(--gray);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .hero-visual-wrapper {
          position: relative;
          /* INCREASED HEIGHT from 600px to 90vh to fill more screen */
          height: 90vh; 
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          perspective: 1000px;
          z-index: 10;
        }

        .hero-visual-card {
          position: relative;
          width: 100%;
          /* INCREASED MAX WIDTH from 500px to 750px */
          max-width: 750px; 
          aspect-ratio: 1 / 1;
          transform-style: preserve-3d;
          transition: transform 0.1s ease-out;
        }

        .visual-ring {
          position: absolute;
          border-radius: 50%;
          border: 2px solid;
          animation: rotate 20s linear infinite;
          pointer-events: none;
        }

        .ring-1 {
          width: 100%;
          height: 100%;
          border-color: rgba(252, 82, 0, 0.2);
          animation-duration: 20s;
        }

        .ring-2 {
          width: 85%;
          height: 85%;
          border-color: rgba(0, 212, 255, 0.2);
          animation-duration: 15s;
          animation-direction: reverse;
        }

        .ring-3 {
          width: 70%;
          height: 70%;
          border-color: rgba(139, 92, 246, 0.2);
          animation-duration: 25s;
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .visual-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 75%;
          height: 75%;
          border-radius: 50%;
          overflow: hidden;
          z-index: 1;
          box-shadow: 0 0 50px rgba(0,0,0,0.8);
          border: 4px solid rgba(255,255,255,0.1);
        }

        .visual-center img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          filter: brightness(0.9) contrast(1.1);
        }

        /* NEW: Glassmorphism Dashboard Overlay */
        .dashboard-overlay {
          position: absolute;
          bottom: 8%;
          left: 5%;
          right: 5%;
          background: rgba(10, 14, 39, 0.75);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 20px;
          padding: 20px;
          z-index: 10;
          transform: translateZ(30px);
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 10px 40px rgba(0,0,0,0.5);
          animation: slideUp 1s ease-out 0.5s backwards;
        }

        .dash-stat {
          text-align: center;
          flex: 1;
        }

        .dash-label {
          font-size: 10px;
          color: var(--gray);
          text-transform: uppercase;
          margin-bottom: 4px;
          letter-spacing: 0.5px;
        }

        .dash-value {
          font-size: 18px;
          font-weight: 700;
          font-variant-numeric: tabular-nums;
          color: var(--light);
        }

        .dash-stat.highlight .dash-value {
          color: var(--secondary);
        }

        .dash-stat.active .dash-value {
          color: var(--primary);
        }

        /* NEW: Floating Mini Cards */
        .mini-float-card {
          position: absolute;
          background: rgba(10, 14, 39, 0.9);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          z-index: 20;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          animation: floatCard 6s ease-in-out infinite;
          max-width: 180px;
        }

        .float-card-1 {
          top: 10%;
          right: -10px;
          transform: translateZ(50px);
        }

        .float-card-2 {
          bottom: 20%;
          left: -20px;
          animation-delay: 2s;
          transform: translateZ(40px);
        }

        .mini-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05));
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          color: var(--primary);
        }

        .mini-info h5 { font-size: 10px; color: var(--gray); text-transform: uppercase; margin-bottom: 2px; font-weight: 600; margin: 0; }
        .mini-info p { font-size: 16px; font-weight: 700; margin: 0; color: var(--light); }

        @keyframes floatCard {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }

        .animate-float {
          animation: floatHero 6s ease-in-out infinite;
        }

        @keyframes floatHero {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }

        .feature-card {
          position: relative;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          overflow: hidden;
          transition: all 0.4s ease;
          backdrop-filter: blur(10px);
        }

        .feature-card:hover {
          transform: translateY(-8px);
          border-color: var(--primary);
          box-shadow: 0 20px 60px rgba(252, 82, 0, 0.2);
        }

        .feature-card:hover .feature-glow {
          opacity: 1;
        }

        .feature-glow {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(252, 82, 0, 0.1), transparent);
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
        }

        .feature-image-container {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .feature-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s ease;
        }

        .feature-card:hover .feature-image {
          transform: scale(1.1);
        }

        .feature-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(to bottom, transparent, rgba(10, 14, 39, 0.9));
          display: flex;
          align-items: flex-end;
          padding: 20px;
        }

        .feature-icon {
          font-size: 48px;
        }

        .benefits-visual {
          position: relative;
        }

        .floating-card {
          position: absolute;
          background: rgba(10, 14, 39, 0.9);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          padding: 20px;
          display: flex;
          gap: 16px;
          align-items: center;
          animation: floatCard 3s ease-in-out infinite;
        }

        .card-1 {
          top: 40px;
          right: -20px;
          animation-delay: 0s;
        }

        .card-2 {
          bottom: 60px;
          left: -20px;
          animation-delay: 1.5s;
        }

        .mini-chart {
          font-size: 32px;
        }

        .card-title {
          font-size: 12px;
          color: var(--gray);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .card-value {
          font-size: 24px;
          font-weight: 800;
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .testimonial-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 32px;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .testimonial-card:hover {
          transform: translateY(-4px);
          border-color: var(--primary);
        }

        .testimonial-stars {
          color: var(--primary);
          font-size: 20px;
          margin-bottom: 16px;
        }

        .testimonial-text {
          font-size: 16px;
          line-height: 1.8;
          margin-bottom: 24px;
          color: #e2e8f0;
        }

        .testimonial-author {
          display: flex;
          gap: 16px;
          align-items: center;
        }

        .testimonial-author img {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
        }

        .author-name {
          font-weight: 600;
          margin-bottom: 4px;
        }

        .author-role {
          font-size: 14px;
          color: var(--gray);
        }

        .cta-section {
          background: linear-gradient(135deg, rgba(252, 82, 0, 0.1), rgba(0, 212, 255, 0.1));
          border-radius: 32px;
          margin: 0 5%;
          padding: 80px 40px;
          text-align: center;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .btn-cta {
          padding: 20px 48px;
          font-size: 18px;
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          color: white;
          box-shadow: 0 10px 30px rgba(252, 82, 0, 0.3);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
        }

        .btn-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 40px rgba(252, 82, 0, 0.4);
        }

        .cta-note {
          margin-top: 16px;
          font-size: 14px;
          color: var(--gray);
        }

        .animate-fade-in {
          animation: fadeIn 1s ease-out;
        }

        .animate-slide-up {
          animation: slideUp 1s ease-out;
        }

        .delay-1 {
          animation-delay: 0.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }

        .delay-2 {
          animation-delay: 0.4s;
          opacity: 0;
          animation-fill-mode: forwards;
        }

        .delay-3 {
          animation-delay: 0.6s;
          opacity: 0;
          animation-fill-mode: forwards;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-on-scroll {
          opacity: 0;
          transform: translateY(50px);
          transition: all 0.8s ease-out;
        }

        .animate-on-scroll.visible {
          opacity: 1;
          transform: translateY(0);
        }

        @media (max-width: 968px) {
          .hero-visual-wrapper {
            height: 450px;
            margin-top: 40px;
          }
          
          .visual-ring {
            width: 300px !important;
            height: 300px !important;
          }
          
          .ring-2 {
            width: 240px !important;
            height: 240px !important;
          }
          
          .ring-3 {
            width: 180px !important;
            height: 180px !important;
          }
          
          .visual-center {
            width: 200px !important;
            height: 200px !important;
          }

          .dashboard-overlay {
            padding: 12px;
            bottom: 5%;
          }

          .dash-value {
            font-size: 14px;
          }
        }
      `}</style>

      {/* Animated Background */}
      <div className="gradient-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      {/* Particle Effect */}
      <div className="particles" id="particles"></div>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          flexDirection: { xs: 'column', lg: 'row' },
          padding: { xs: '40px 5%', lg: '0 5%' },
        }}
      >
        {/* Left Content */}
        <Box
          sx={{
            flex: 1,
            maxWidth: { xs: '100%', lg: '550px' },
            zIndex: 20, // Ensures text stays above overlapping visual
            pt: { lg: '120px' } // Centers text vertically against the tall visual
          }}
        >
          <div className="hero-badge animate-fade-in">
            <span className="badge-pulse"></span>
            Powered by AI & Strava
          </div>

          <Typography
            variant={isMobile ? 'h3' : 'h1'}
            component="h1"
            gutterBottom
            className="hero-title animate-slide-up"
            sx={{
              fontWeight: 800,
              mb: 2,
              lineHeight: 1.1,
              fontSize: { xs: '40px', lg: '72px' }
            }}
          >
            Transform Your
            <span className="gradient-text"> Athletic Journey</span>
          </Typography>

          <Typography
            variant="body1"
            className="hero-subtitle animate-slide-up delay-1"
            sx={{
              color: 'var(--gray)',
              mb: 4,
              fontSize: { xs: '16px', lg: '20px' },
              maxWidth: { xs: '100%', lg: '540px' }
            }}
          >
            Next-generation training analytics that adapt to you. Track, plan, and dominate your running with data-driven precision.
          </Typography>

          <Box className="hero-buttons animate-slide-up delay-2" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 6 }}>
            <Button
              variant="contained"
              onClick={handleGetStarted}
              sx={{
                background: 'linear-gradient(135deg, var(--primary), var(--primary-light))',
                color: 'white',
                px: 4,
                py: 2,
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(252, 82, 0, 0.3)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 15px 40px rgba(252, 82, 0, 0.4)'
                }
              }}
            >
              Start Free Trial
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ marginLeft: '8px' }}>
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </Button>
            <Button
              variant="outlined"
              onClick={handleLearnMore}
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                px: 4,
                py: 2,
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '12px',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Explore Features
            </Button>
          </Box>

          <div className="hero-stats animate-fade-in delay-3">
            <div className="stat-item">
              <div className="stat-number">50K+</div>
              <div className="stat-label">Active Athletes</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">2M+</div>
              <div className="stat-label">Workouts Analyzed</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">99.9%</div>
              <div className="stat-label">Uptime</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">4.9★</div>
              <div className="stat-label">User Rating</div>
            </div>
          </div>
        </Box>

        {/* Right Visual: Larger and Closer */}
        <Box
          className="hero-visual-wrapper"
          sx={{ flex: 1.2 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <div
            ref={heroVisualRef}
            className="hero-visual-card"
            style={{
              transform: `perspective(1000px) rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`
            }}
          >
            <div className="visual-ring ring-1"></div>
            <div className="visual-ring ring-2"></div>
            <div className="visual-ring ring-3"></div>

            {/* Running Image */}
            <div className="visual-center">
              <img
                src="https://images.unsplash.com/photo-1758506971986-b0d0edebd8d5?q=80&w=987&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                alt="Runner in motion"
              />
            </div>

            {/* Dashboard Overlay */}
            <div className="dashboard-overlay">
              <div className="dash-stat highlight">
                <div className="dash-label">Avg Pace</div>
                <div className="dash-value">5:24 /km</div>
              </div>
              <div className="dash-stat active">
                <div className="dash-label">Heart Rate</div>
                <div className="dash-value">162 BPM</div>
              </div>
              <div className="dash-stat">
                <div className="dash-label">Distance</div>
                <div className="dash-value">12.5 km</div>
              </div>
            </div>

            {/* Floating Mini Cards */}
            <div className="mini-float-card float-card-1">
              <div className="mini-icon">
                <FlashOn />
              </div>
              <div className="mini-info">
                <h5>Power Est.</h5>
                <p className="gradient-text">285W</p>
              </div>
            </div>

            <div className="mini-float-card float-card-2">
              <div className="mini-icon">
                <LocalFireDepartment />
              </div>
              <div className="mini-info">
                <h5>Load (CTL)</h5>
                <p className="gradient-text">High</p>
              </div>
            </div>
          </div>
        </Box>
      </Box>

      {/* Features Section */}
      <Box sx={{ position: 'relative', zIndex: 2, py: { xs: 8, md: 12 }, px: { xs: 3, md: 5 } }} ref={featuresRef}>
        <Box className="section-header animate-on-scroll" sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant={isMobile ? 'h3' : 'h2'}
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '32px', md: '56px' }
            }}
          >
            Everything You Need to
            <span className="gradient-text"> Excel</span>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'var(--gray)',
              maxWidth: '600px',
              mx: 'auto',
              fontSize: '18px'
            }}
          >
            Cutting-edge tools designed for athletes who demand more from their training
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <div className="feature-card animate-on-scroll">
                <div className="feature-image-container">
                  <img src={feature.image} alt={feature.title} className="feature-image" />
                  <div className="feature-overlay">
                    <div className="feature-icon">{feature.icon}</div>
                  </div>
                </div>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 700, fontSize: '22px' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--gray)', fontSize: '15px', lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </Box>
                <div className="feature-glow"></div>
              </div>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ position: 'relative', zIndex: 2, py: { xs: 8, md: 12 }, px: { xs: 3, md: 5 } }}>
        <Grid container spacing={8} alignItems="center" className="benefits-content animate-on-scroll">
          <Grid item xs={12} md={6}>
            <Typography
              variant={isMobile ? 'h3' : 'h2'}
              component="h2"
              gutterBottom
              className="benefits-title"
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: '32px', md: '48px' }
              }}
            >
              Train Smarter,
              <span className="gradient-text"> Not Harder</span>
            </Typography>
            <Typography
              variant="body1"
              className="benefits-description"
              sx={{
                color: 'var(--gray)',
                mb: 4,
                fontSize: '18px',
                lineHeight: 1.8
              }}
            >
              Our platform combines cutting-edge sports science with intuitive design.
              Whether you're a weekend warrior or elite athlete, get personalized insights
              that drive real performance gains.
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box component="li" sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box className="check-icon" sx={{
                  width: 24,
                  height: 24,
                  background: 'linear-gradient(135deg, var(--primary), var(--primary-light))',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  ✓
                </Box>
                <Box>
                  <Typography component="strong" sx={{ display: 'block', mb: 1, fontSize: '18px' }}>
                    Real-time Performance Tracking
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--gray)', fontSize: '14px' }}>
                    Monitor training load, recovery, and fatigue in real-time
                  </Typography>
                </Box>
              </Box>
              <Box component="li" sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box className="check-icon" sx={{
                  width: 24,
                  height: 24,
                  background: 'linear-gradient(135deg, var(--primary), var(--primary-light))',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  ✓
                </Box>
                <Box>
                  <Typography component="strong" sx={{ display: 'block', mb: 1, fontSize: '18px' }}>
                    Predictive Analytics
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--gray)', fontSize: '14px' }}>
                    AI-powered insights forecast your race-day performance
                  </Typography>
                </Box>
              </Box>
              <Box component="li" sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box className="check-icon" sx={{
                  width: 24,
                  height: 24,
                  background: 'linear-gradient(135deg, var(--primary), var(--primary-light))',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  ✓
                </Box>
                <Box>
                  <Typography component="strong" sx={{ display: 'block', mb: 1, fontSize: '18px' }}>
                    Community & Coaching
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--gray)', fontSize: '14px' }}>
                    Connect with coaches and athletes worldwide
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} className="benefits-visual">
            <Box sx={{ position: 'relative' }}>
              <img src="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&q=80" alt="Training Analytics" className="benefits-image" style={{ width: '100%', height: '500px', objectFit: 'cover', borderRadius: '24px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)' }} />
              <Box className="floating-card card-1">
                <Box className="mini-chart">📈</Box>
                <Box>
                  <Box className="card-title">CTL</Box>
                  <Box className="card-value">142</Box>
                </Box>
              </Box>
              <Box className="floating-card card-2">
                <Box className="mini-chart">💪</Box>
                <Box>
                  <Box className="card-title">Form</Box>
                  <Box className="card-value">+8</Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ position: 'relative', zIndex: 2, py: { xs: 8, md: 12 }, px: { xs: 3, md: 5 } }}>
        <Box className="testimonial-container animate-on-scroll">
          <Typography
            variant={isMobile ? 'h3' : 'h2'}
            component="h2"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 800,
              mb: 6,
              fontSize: { xs: '32px', md: '56px' }
            }}
          >
            Trusted by Athletes Worldwide
          </Typography>
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <div className="testimonial-card">
                  <div className="testimonial-stars">★★★★★</div>
                  <Typography className="testimonial-text" variant="body1">
                    {testimonial.text}
                  </Typography>
                  <Box className="testimonial-author">
                    <img src={testimonial.image} alt={testimonial.name} />
                    <Box>
                      <Typography className="author-name" variant="subtitle1">
                        {testimonial.name}
                      </Typography>
                      <Typography className="author-role" variant="body2">
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                </div>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* CTA Section */}
      <Box className="cta-section animate-on-scroll" sx={{ position: 'relative', zIndex: 2 }}>
        <Box className="cta-content">
          <Typography
            variant={isMobile ? 'h3' : 'h2'}
            component="h2"
            gutterBottom
            className="cta-title"
            sx={{
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '32px', md: '56px' }
            }}
          >
            Ready to Reach Your Peak?
          </Typography>
          <Typography
            variant="body1"
            className="cta-subtitle"
            sx={{
              color: 'var(--gray)',
              mb: 4,
              fontSize: '20px'
            }}
          >
            Join thousands of athletes using data to unlock their full potential
          </Typography>
          <Button
            className="btn-cta"
            onClick={handleGetStarted}
          >
            Connect with Strava
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '8px' }}>
              <path d="M13.5 4.5L21 12M21 12L13.5 19.5M21 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </Button>
          <Typography className="cta-note" variant="body2">
            Free 14-day trial • No credit card required
          </Typography>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        className="footer"
        sx={{
          position: 'relative',
          zIndex: 2,
          py: { xs: 6, md: 8 },
          px: { xs: 3, md: 5 },
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          mt: 10
        }}
      >
        <Grid container spacing={6} className="footer-content">
          <Grid item xs={12} md={4} className="footer-brand">
            <Typography variant="h3" component="h3" gutterBottom sx={{ fontSize: '24px', fontWeight: 600 }}>
              Surge
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--gray)' }}>
              Elite training analytics for elite athletes
            </Typography>
          </Grid>
          <Grid item xs={12} md={8} className="footer-links">
            <Grid container spacing={4}>
              <Grid item xs={4}>
                <Typography variant="h4" component="h4" gutterBottom sx={{ fontSize: '16px', fontWeight: 600 }}>
                  Product
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography component="a" href="#features" sx={{ color: 'var(--gray)', mb: 1.5, textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}>
                    Features
                  </Typography>
                  <Typography component="a" href="#pricing" sx={{ color: 'var(--gray)', mb: 1.5, textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}>
                    Pricing
                  </Typography>
                  <Typography component="a" href="#updates" sx={{ color: 'var(--gray)', mb: 1.5, textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}>
                    Updates
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h4" component="h4" gutterBottom sx={{ fontSize: '16px', fontWeight: 600 }}>
                  Company
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography component="a" href="#about" sx={{ color: 'var(--gray)', mb: 1.5, textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}>
                    About
                  </Typography>
                  <Typography component="a" href="#blog" sx={{ color: 'var(--gray)', mb: 1.5, textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}>
                    Blog
                  </Typography>
                  <Typography component="a" href="#careers" sx={{ color: 'var(--gray)', mb: 1.5, textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}>
                    Careers
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h4" component="h4" gutterBottom sx={{ fontSize: '16px', fontWeight: 600 }}>
                  Support
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography component="a" href="#help" sx={{ color: 'var(--gray)', mb: 1.5, textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}>
                    Help Center
                  </Typography>
                  <Typography component="a" href="#contact" sx={{ color: 'var(--gray)', mb: 1.5, textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}>
                    Contact
                  </Typography>
                  <Typography component="a" href="#api" sx={{ color: 'var(--gray)', mb: 1.5, textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}>
                    API Docs
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Box className="footer-bottom" sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: 4,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'var(--gray)',
          fontSize: '14px',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 }
        }}>
          <Typography variant="body2">
            © 2025 Surge. Built with React & Vite.
          </Typography>
          <Box className="footer-legal" sx={{ display: 'flex', gap: 3 }}>
            <Typography component="a" href="#privacy" sx={{ color: 'var(--gray)', textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}>
              Privacy
            </Typography>
            <Typography component="a" href="#terms" sx={{ color: 'var(--gray)', textDecoration: 'none', '&:hover': { color: 'var(--primary)' } }}>
              Terms
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingPage;